// Owner: create preset wizard
import { apiFetch } from '../../core/api.js';
import {
  buildPresetSpecs,
  escapeAttr,
  formatPriceWithRub,
  presetSpecsPairs
} from '../../core/format.js';
import { compressImageFile } from '../../core/images.js';
import { getTg } from '../../core/telegram.js';
import { getCurrentUser } from '../../app/app-state.js';
import { showView } from '../../app/navigation.js';
import { getInput, getTextArea, getEl } from '../../core/dom.js';
import {
  normalizeParcelDraft,
  fillCreateWizardParcelInputs,
  readCreateWizardParcelInputs,
  formatParcelReview
} from './parcel.js';
import { getCreateDraft, resetCreateDraft } from './create-draft.js';
import type { ApiResponse } from '../../types/api.js';
import { initPresetSpecAutocomplete } from '../build/components-autocomplete.js';

resetCreateDraft();
initPresetSpecAutocomplete();

function createStep1Next(): void {
  const titleEl = getInput('create-step-title');
  const errEl = document.getElementById('create-step-title-error');
  const v = (titleEl?.value || '').trim();
  if (!v) {
    titleEl?.classList.add('form-input-error');
    if (errEl) errEl.textContent = 'Заполните поле.';
    return;
  }
  titleEl?.classList.remove('form-input-error');
  if (errEl) errEl.textContent = '';
  getCreateDraft().title = v;
  getCreateDraft().shortDescription = (getInput('create-step-subtitle')?.value || '').trim();
  showView('view-owner-create-2', 'right');
  const d = getTextArea('create-step-desc');
  const specs = buildPresetSpecs(getCreateDraft());
  const cpu = getInput('create-step-cpu');
  const gpu = getInput('create-step-gpu');
  const motherboard = getInput('create-step-motherboard');
  const ram = getInput('create-step-ram');
  const psu = getInput('create-step-psu');
  const storage = getInput('create-step-storage');
  const caseName = getInput('create-step-case');
  if (d) d.value = getCreateDraft().description || '';
  if (cpu) cpu.value = specs.cpu;
  if (gpu) gpu.value = specs.gpu;
  if (motherboard) motherboard.value = specs.motherboard;
  if (ram) ram.value = specs.ram;
  if (psu) psu.value = specs.psu;
  if (storage) storage.value = specs.storage;
  if (caseName) caseName.value = specs.caseName;
}

function createStep2Next(): void {
  const descEl = getTextArea('create-step-desc');
  const errEl = document.getElementById('create-step-desc-error');
  const v = (descEl?.value || '').trim();
  if (!v) {
    descEl?.classList.add('form-textarea-error');
    if (errEl) errEl.textContent = 'Заполните поле.';
    return;
  }
  descEl?.classList.remove('form-textarea-error');
  if (errEl) errEl.textContent = '';
  getCreateDraft().description = v;
  getCreateDraft().specs = {
    cpu: (getInput('create-step-cpu')?.value || '').trim(),
    gpu: (getInput('create-step-gpu')?.value || '').trim(),
    motherboard: (getInput('create-step-motherboard')?.value || '').trim(),
    ram: (getInput('create-step-ram')?.value || '').trim(),
    psu: (getInput('create-step-psu')?.value || '').trim(),
    storage: (getInput('create-step-storage')?.value || '').trim(),
    caseName: (getInput('create-step-case')?.value || '').trim()
  };
  showView('view-owner-create-3', 'right');
  const p = getInput('create-step-price');
  if (p) p.value = getCreateDraft().price || '';
  const op = getInput('create-step-old-price');
  if (op) op.value = getCreateDraft().oldPrice || '';
  const a = getInput('create-step-avito');
  if (a) a.value = getCreateDraft().avitoLink || '';
  fillCreateWizardParcelInputs(getCreateDraft().parcel);
}

let replacePhotoIndex: number | null = null;

function renderCreateStep4Gallery(): void {
  const listEl = document.getElementById('create-step-preview-list');
  const wrap = document.getElementById('create-step-image-wrap');
  if (!listEl || !wrap) return;
  const imgs = getCreateDraft().images || [];
  let html = '';
  for (let idx = 0; idx < imgs.length; idx++) {
    html += '<div class="photo-preview-card" data-photo-index="' + idx + '">';
    html += '<img src="' + escapeAttr(imgs[idx]) + '" alt="" />';
    if (idx === 0) html += '<span class="photo-preview-badge">Главная</span>';
    html += '<button type="button" class="photo-preview-edit" data-photo-index="' + idx + '" aria-label="Изменить фото">';
    html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
    html += '</button></div>';
  }
  listEl.innerHTML = html;
  listEl.querySelectorAll('.photo-preview-edit').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      replacePhotoIndex = parseInt(btn.getAttribute('data-photo-index') || '0', 10);
      getInput('create-step-image')?.click();
    });
  });
  wrap.classList.toggle('photo-add-inline', imgs.length > 0);
}

function createStep3Next(): void {
  const priceEl = getInput('create-step-price');
  getCreateDraft().price = (priceEl?.value || '').trim();
  const oldPriceEl = getInput('create-step-old-price');
  getCreateDraft().oldPrice = (oldPriceEl?.value || '').trim();
  const avitoEl = getInput('create-step-avito');
  getCreateDraft().avitoLink = (avitoEl?.value || '').trim();
  getCreateDraft().parcel = normalizeParcelDraft(readCreateWizardParcelInputs());
  showView('view-owner-create-4', 'right');
  renderCreateStep4Gallery();
}

function createStep4Next(): void {
  const wrap = document.getElementById('create-step-image-wrap');
  const errEl = document.getElementById('create-step-image-error');
  if (errEl) errEl.textContent = '';
  wrap?.classList.remove('form-input-error');
  const imgs = getCreateDraft().images || [];
  if (!imgs.length) {
    wrap?.classList.add('form-input-error');
    if (errEl) errEl.textContent = 'Добавьте хотя бы одно фото.';
    return;
  }
  getCreateDraft().image = imgs[0];
  goToCreateStep5();
}

function goToCreateStep5(): void {
  const titleEl = document.getElementById('review-title');
  const subtitleEl = document.getElementById('review-subtitle');
  const specsEl = document.getElementById('review-specs');
  const descEl = document.getElementById('review-desc');
  const priceEl = document.getElementById('review-price');
  const oldPriceEl = document.getElementById('review-old-price');
  const photoEl = document.getElementById('review-photo');
  const avitoEl = document.getElementById('review-avito');
  if (titleEl) titleEl.textContent = getCreateDraft().title || '—';
  if (subtitleEl) {
    subtitleEl.textContent = getCreateDraft().shortDescription || '—';
  }
  if (specsEl) {
    const lines = presetSpecsPairs(getCreateDraft().specs).map(function (pair) {
      return pair[0] + ': ' + (pair[1] || '—');
    });
    specsEl.textContent = lines.join('\n');
  }
  if (descEl) {
    const desc = getCreateDraft().description || '—';
    descEl.textContent = desc.slice(0, 200) + (getCreateDraft().description && getCreateDraft().description.length > 200 ? '…' : '');
  }
  if (priceEl) priceEl.textContent = getCreateDraft().price || '—';
  if (oldPriceEl) oldPriceEl.textContent = getCreateDraft().oldPrice || '—';
  if (avitoEl) {
    const link = getCreateDraft().avitoLink;
    avitoEl.textContent = link ? String(link).slice(0, 50) + (String(link).length > 50 ? '…' : '') : '—';
  }
  if (photoEl) {
    photoEl.textContent =
      getCreateDraft().images && getCreateDraft().images.length
        ? 'Добавлено ' + getCreateDraft().images.length + ' фото'
        : 'Нет';
  }
  const reviewParcel = document.getElementById('review-parcel');
  if (reviewParcel) reviewParcel.textContent = formatParcelReview(getCreateDraft().parcel);
  showView('view-owner-create-5', 'right');
}

const createImageInput = getInput('create-step-image');
if (createImageInput) {
  createImageInput.addEventListener('change', function () {
    const wrap = document.getElementById('create-step-image-wrap');
    const errEl = document.getElementById('create-step-image-error');
    if (!this.files || !this.files[0]) return;
    const file = this.files[0];
    compressImageFile(file, 1280, 0.8)
      .then(function (dataUrl: string) {
        if (errEl) errEl.textContent = '';
        wrap?.classList.remove('form-input-error');
        const idx = replacePhotoIndex;
        replacePhotoIndex = null;
        if (typeof idx === 'number' && idx >= 0 && getCreateDraft().images && getCreateDraft().images[idx] !== undefined) {
          getCreateDraft().images[idx] = dataUrl;
        } else {
          if (!getCreateDraft().images) getCreateDraft().images = [];
          getCreateDraft().images.push(dataUrl);
        }
        renderCreateStep4Gallery();
      })
      .catch(function () {
        wrap?.classList.add('form-input-error');
        if (errEl) errEl.textContent = 'Не удалось прочитать файл.';
      });
    this.value = '';
  });
}

document.getElementById('create-step-1-next')?.addEventListener('click', createStep1Next);
document.getElementById('create-step-2-next')?.addEventListener('click', createStep2Next);
document.getElementById('create-step-3-next')?.addEventListener('click', createStep3Next);
document.getElementById('create-step-4-next')?.addEventListener('click', createStep4Next);

const publishBtn = getEl<HTMLButtonElement>('create-step-5-publish');
if (publishBtn) {
  publishBtn.addEventListener('click', async function () {
    const t = getTg();
    if (!t || !getCurrentUser()) return;
    publishBtn.disabled = true;
    publishBtn.textContent = 'Публикация...';
    try {
      const priceFormatted = formatPriceWithRub(getCreateDraft().price);
      const oldPriceFormatted = formatPriceWithRub(getCreateDraft().oldPrice);
      const imgs = getCreateDraft().images || [];
      const mainImage = imgs.length ? imgs[0] : getCreateDraft().image || '';
      const res = await apiFetch('/api/presets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: getCreateDraft().title,
          shortDescription: getCreateDraft().shortDescription,
          price: priceFormatted,
          oldPrice: oldPriceFormatted,
          image: mainImage,
          images: imgs,
          description: getCreateDraft().description,
          specs: buildPresetSpecs(getCreateDraft().specs),
          avitoLink: getCreateDraft().avitoLink,
          parcel: normalizeParcelDraft(getCreateDraft().parcel)
        })
      });
      let data: ApiResponse | null = null;
      try {
        data = (await res.json()) as ApiResponse;
      } catch (_) {
        data = { ok: false };
      }
      if (res.status === 413) {
        t.showPopup?.({
          title: 'Ошибка',
          message: 'Фото слишком большое. Выберите изображение до 2–3 МБ.',
          buttons: [{ type: 'ok' }]
        });
      } else if (!data || !data.ok) {
        let msg = 'Не удалось сохранить предложение.';
        if (res.status === 403) msg = 'Нет доступа. Войдите как владелец.';
        else if (res.status === 400) msg = 'Заполните название объявления.';
        t.showPopup?.({ title: 'Ошибка', message: msg, buttons: [{ type: 'ok' }] });
      } else {
        resetCreateDraft();
        showView('view-owner-presets-ready');
        t.showPopup?.({ title: 'Готово', message: 'Объявление опубликовано.', buttons: [{ type: 'ok' }] });
      }
    } catch (_) {
      t.showPopup?.({
        title: 'Ошибка',
        message: 'Нет связи или сервер недоступен. Проверьте интернет.',
        buttons: [{ type: 'ok' }]
      });
    } finally {
      publishBtn.disabled = false;
      publishBtn.textContent = 'Опубликовать';
    }
  });
}
